import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { formatDate, formatMoney } from '@tabita/i18n';
import { useI18n } from '@/i18n/i18n-provider';
import { financeApi } from '@/services/finance.api';
import { ApiError } from '@/services/api-client';
import { Alert, EmptyState, Spinner } from '@/components/ui';

export function AccountLedgerPage() {
  const { t, locale } = useI18n();
  const { id } = useParams<{ id: string }>();

  const accountsQuery = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: () => financeApi.listAccounts(),
  });
  const ledgerQuery = useQuery({
    queryKey: ['finance', 'ledger', id],
    queryFn: () => financeApi.getAccountLedger(id!),
    enabled: Boolean(id),
  });

  const account = accountsQuery.data?.find((a) => a.id === id);
  const lines = ledgerQuery.data ?? [];

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="page-title">
          {t('finance.ledger.title', { values: { account: account?.name ?? '' } })}
        </h1>
      </header>

      <Link to="/finance" className="auth-secondary-link">
        {t('finance.ledger.back')}
      </Link>

      {ledgerQuery.isError && (
        <Alert tone="danger">
          {ledgerQuery.error instanceof ApiError
            ? t(`errors.${ledgerQuery.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {ledgerQuery.isPending && <Spinner label={t('common.states.loading')} />}

      {ledgerQuery.isSuccess && lines.length === 0 && (
        <EmptyState title={t('finance.journalEntry.empty')} />
      )}

      {lines.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('finance.journalEntry.date')}</th>
                <th scope="col">{t('finance.journalEntry.description')}</th>
                <th scope="col">{t('finance.journalEntry.debit')}</th>
                <th scope="col">{t('finance.journalEntry.credit')}</th>
                <th scope="col">{t('finance.ledger.balance')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => (
                <tr key={`${line.journalEntryId}-${index}`}>
                  <td dir="ltr">{formatDate(line.entryDate, locale)}</td>
                  <td>{line.description}</td>
                  <td dir="ltr">
                    {line.debit > 0 ? (
                      formatMoney(line.debit, 'USD', locale)
                    ) : (
                      <span className="cell-empty">—</span>
                    )}
                  </td>
                  <td dir="ltr">
                    {line.credit > 0 ? (
                      formatMoney(line.credit, 'USD', locale)
                    ) : (
                      <span className="cell-empty">—</span>
                    )}
                  </td>
                  <td dir="ltr">{formatMoney(line.balance, 'USD', locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
